import * as Three from 'three';
import {getVertexRenderCoords, converVerticesObjToArr} from "../helper";
import Wall3D from "./wall-3d";
import {rotate2DPoints} from "../libs/mathematics";
import {assignUVs, applyTexture} from '../three-helper';
import FloorplanManager from "../../../service/e3.plan.manager";

class Area3D {
    _types = {
        'floor': {
            type: 'floor',
            renderSide: Three.FrontSide,
            coordIndex: 0,
        },
        'ceiling': {
            type: 'ceiling',
            renderSide: Three.BackSide,
            coordIndex: 1,
        }
    };

    constructor(type = 'floor') {
        this._config = this._types[type];
    }

    render3D(element, layer, scene, texture = null) {
        const simplify = vertices => {
            const distance = (a, b) => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));
            const onLine = (a, b, c) => distance(a, b) + distance(b, c) - distance(a, c) === 0;

            for (let i = 0; i < vertices.length - 2; i++) {
                if (onLine(vertices[i], vertices[i + 1], vertices[i + 2])) {
                    vertices.splice(i + 1, 1);

                    return simplify(vertices)
                }
            }

            if (onLine(vertices[vertices.length - 2], vertices[vertices.length - 1], vertices[0])) {
                vertices.splice(vertices.length - 1, 1);

                return simplify(vertices)
            }

            if (onLine(vertices[vertices.length - 1], vertices[0], vertices[1])) {
                vertices.splice(0, 1);

                return simplify(vertices)
            }

            return vertices;
        }

        let shape = new Three.Shape();
        let {renderSide} = this._config;

        if (element.vertices) {
          element.vertices = simplify(element.vertices.map(id => ({
            x: layer.vertices[id].x,
            y: layer.vertices[id].y,
            id: id})
          )).map(v => v.id);
        }

        let vertices = element.vertices.map(vertexID => getVertexRenderCoords(layer.vertices[vertexID], scene.unit));

        shape.moveTo(vertices[0].x, vertices[0].y);
        vertices.forEach(vertex => shape.lineTo(vertex.x, vertex.y));

        let shapeGeometry = new Three.ShapeGeometry(shape);
        shapeGeometry = assignUVs(shapeGeometry);

        let verticesWithoutRotate = this._getVerticesArea(element, layer);
        shapeGeometry.faceVertexUvs[0] = this._getFaceVertexUvs(verticesWithoutRotate);

        let areaMaterial = new Three.MeshPhongMaterial({
            side: renderSide,
            opacity: 1,
            depthWrite: false,
        });

        return !texture
            ? this._loadTexture(element, areaMaterial, shapeGeometry)
            : this._splitTexture(texture, areaMaterial, shapeGeometry, element, layer)
    }

    updateRender3D(element, layer, scene, mesh, oldElement, differences, selfDestroy, selfBuild) {
        let noPerf = () => { selfDestroy(); return selfBuild(); };

        if( differences[0] === 'properties' && differences[1] === 'texture' ){
            if( differences[1] === 'texture' ) {
                return noPerf();
            }
        }
        else return noPerf();

        return Promise.resolve(mesh);
    }

    _splitTexture(texture, areaMaterial, shapeGeometry, element, layer) {
        const areaId = element.id,
            ceiling = element.properties.scene.ceiling >>> 0;
        let {posePitch, poseRoll} = element.properties.scene;
        let {coordIndex, type} = this._config;
        let coordCart =  element.vertices.map((id) => layer.vertices[id]['2d'][coordIndex]);

        return texture.splitTexture(coordCart, areaId, ceiling, type, false, {poseRoll, posePitch})
            .then((data) => {
                let uri = data.src;
                let areaTexture = Wall3D.createCustomTexture(uri, type);
                 this._sendPostMsg(element.name, areaTexture.uri, type)

                return applyTexture(areaMaterial, areaTexture).then(() => data)
            }).then((data) => {
                let floor = new Three.Mesh(shapeGeometry, areaMaterial);

                floor.rotation.x -= Math.PI / 2;
                floor.name = type;
                floor.userData['sceneID'] = areaId;

                return {obj: floor, data};
            });
    }

    _sendPostMsg(name, uri, type) {
        window.postMessage({
            event: 'loadTexture',
            name,
            type,
            url: uri
        }, '*');
    }

    _loadTexture(element, areaMaterial, shapeGeometry) {
        let {type} = this._config;
        const uri = element.texture[type];
        let areaTexture = Wall3D.createCustomTexture(uri, type);

        return applyTexture(areaMaterial, areaTexture).then(() => {
            let floor = new Three.Mesh(shapeGeometry, areaMaterial);

            floor.rotation.x -= Math.PI / 2;
            floor.name = type;

            return {obj:floor};
        });
    }

    _getFaceVertexUvs(vertices) {
        let shape = new Three.Shape();

        shape.moveTo(vertices[0].x, vertices[0].y);
        vertices.forEach(vertex => shape.lineTo(vertex.x, vertex.y));

        let geometry = new Three.ShapeGeometry(shape);

        geometry = assignUVs(geometry);

        return geometry.faceVertexUvs[0];
    }

    _getVerticesArea(area, layer) {
        let vertices = {};
        let {camera: {x: cameraX,y: cameraY}, angleForRotateCustomUpdate, rotateDirection} = area.properties.scene;
        const height_lens = FloorplanManager.getHeightLens(area);
        area.vertices.forEach(vid => {
            let vertex = layer.vertices[vid];
            vertices[vid] = [
                (vertex.x - cameraX) / (height_lens),
                (vertex.y - cameraY) / (height_lens)
            ];
        });
        if(angleForRotateCustomUpdate) rotateDirection += angleForRotateCustomUpdate;

        vertices = rotate2DPoints(vertices, -rotateDirection - (layer.angle || 0) * Math.PI / 180);

        return converVerticesObjToArr(vertices);
    }
}

export default Area3D;
